import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import Cropper from "react-easy-crop";
import { ArrowLeft, User, Cake, Briefcase, Camera, FloppyDisk, House, MagnifyingGlassPlus, X, PlayCircle, Sparkle, DotsSixVertical } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import cropImage from "../utils/cropImage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Read a file into a data URL without any resizing so the cropper has full resolution
// to work with. Final resize happens inside `cropImage` after the user confirms.
const fileToRawDataUrl = (file) => new Promise((resolve, reject) => {
  if (!file) return reject(new Error("No file"));
  if (file.size > 12 * 1024 * 1024) return reject(new Error("Image over 12MB"));
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error("Read failed"));
  reader.readAsDataURL(file);
});

// Format MM-DD to "April 17" for display.
const formatBirthday = (mmdd) => {
  if (!mmdd || mmdd.length !== 5) return "";
  try {
    const [m, d] = mmdd.split("-");
    const date = new Date(2000, parseInt(m) - 1, parseInt(d));
    return date.toLocaleDateString("en-US", { month: "long", day: "numeric" });
  } catch { return mmdd; }
};

export default function ProfilePage({ user, onBack, onGoHome, onUpdated, onOpenKeepsake }) {
  const [me, setMe] = useState(null);
  const [photo, setPhoto] = useState("");
  // Track whether the user has actually touched the photo in this session so
  // we don't overwrite the saved DB photo with an empty string when they're
  // just editing other fields (birthday, email, etc.) and the initial crew
  // fetch raced/was empty. Photo is only sent to the server when the user
  // crops a NEW image (or explicitly removes the current one).
  const [photoDirty, setPhotoDirty] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [hireDate, setHireDate] = useState("");
  const [birthday, setBirthday] = useState("");
  // Personal "How do you let go and have fun?" line. Free text, 140-char
  // cap on save (server also caps). Shows on the crew tile detail sheet
  // so the team gets to know each other beyond just stats. (User ask
  // 2026-05-25.)
  const [funMotto, setFunMotto] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  // Cropper state — set when the user picks a file, cleared on cancel/confirm.
  const [rawImage, setRawImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedPixels, setCroppedPixels] = useState(null);
  const [cropping, setCropping] = useState(false);

  const onCropComplete = useCallback((_, areaPixels) => setCroppedPixels(areaPixels), []);

  useEffect(() => {
    if (!user?.crew_id) return;
    // Dedicated single-record endpoint is MUCH lighter than pulling the full
    // crew list (which can be 2MB+ with photo data URLs). Perf fix 2026-05-02.
    axios.get(`${API}/crew/${user.crew_id}`).then((r) => {
      const record = r.data;
      if (record) {
        setMe(record);
        setPhoto(record.photo_url || "");
        setFirstName(record.first_name || "");
        setHireDate(record.hire_date || "");
        setBirthday(record.birthday || "");
        setFunMotto(record.fun_motto || "");
      }
    }).catch(() => toast.error("Failed to load profile"));
  }, [user?.crew_id]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await fileToRawDataUrl(file);
      setRawImage(dataUrl);
      // Reset cropper to centered default when loading a new image.
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    } catch (err) {
      toast.error(err.message || "Could not load image");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleCropConfirm = async () => {
    if (!rawImage || !croppedPixels) return;
    setCropping(true);
    try {
      const dataUrl = await cropImage(rawImage, croppedPixels, 512);
      setPhoto(dataUrl);
      setRawImage(null);
      // Auto-save the photo immediately. Users expect a profile picture to
      // stick the moment they pick it — previously it only lived in local
      // state until a separate Save tap, so it silently never persisted and
      // other screens kept the old photo. (user report 2026-06)
      try {
        await axios.put(
          `${API}/crew/${user.crew_id}/profile`,
          { photo_url: dataUrl },
          { params: { actor_id: user.crew_id } },
        );
        setPhotoDirty(false);
        // Refresh the app-wide roster so the version-stamped avatar URL
        // updates everywhere (WOW CREW, home, slide-out menu, stories).
        try { window.dispatchEvent(new Event("wow:refresh-crew")); } catch { /* ignore */ }
        onUpdated?.();
        toast.success("Profile photo updated");
      } catch (err) {
        setPhotoDirty(true); // save failed → let the Save button retry
        toast.error(err.response?.data?.detail || "Couldn't save photo — tap Save to retry");
      }
    } catch (err) {
      toast.error(err.message || "Could not crop image");
    } finally {
      setCropping(false);
    }
  };

  const handleReCrop = () => {
    if (!photo) return;
    // Let the user re-frame their current saved photo.
    setRawImage(photo);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const handleSave = async () => {
    // Only persist a birthday when both month + day are chosen; otherwise clear it.
    const cleanBirthday = /^\d{2}-\d{2}$/.test(birthday) ? birthday : "";
    if (birthday && !cleanBirthday) {
      toast.error("Please pick both a month and a day for your birthday");
      return;
    }
    setSaving(true);
    try {
      // Only include photo_url in the PUT payload when the user actually
      // changed it in this session. This prevents accidentally wiping a saved
      // DB photo if the initial crew fetch was slow/empty and local state
      // still shows "" — a real regression reported 2026-05-02 where users
      // were losing their profile pictures after saving unrelated fields.
      //
      // Same principle for hire_date: only send it when the user actually
      // changed it, otherwise a photo-only save falsely trips the "please
      // update my work anniversary" notification to managers. (user report
      // 2026-05-02)
      const body = {
        first_name: firstName.trim() || me?.first_name,
        birthday: cleanBirthday,
        fun_motto: funMotto.trim().slice(0, 140),
      };
      if (photoDirty) body.photo_url = photo;
      if (hireDate && hireDate !== (me?.hire_date || "")) {
        body.hire_date = hireDate;
      }
      const res = await axios.put(`${API}/crew/${user.crew_id}/profile`, body, { params: { actor_id: user.crew_id } });
      // Backend flags `_pending_request` when a locked crew requests an
      // anniversary change — it parks the date on `hire_date_pending` and
      // fires an Inbox card to managers. Show a different toast so the user
      // knows it wasn't an instant save.
      if (res.data?._pending_request) {
        toast.success("Request sent — your manager will approve it in their Inbox");
        // Refresh local view so the "Pending approval" chip shows immediately.
        setMe(res.data);
        setHireDate(res.data.hire_date || "");
      } else {
        toast.success("Profile updated");
      }
      setPhotoDirty(false); // photo saved (or not sent) → back to clean state
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const initial = (firstName || "?").charAt(0).toUpperCase();

  return (
    <div
      className="min-h-screen relative"
      style={{
        background:
          "radial-gradient(1000px 700px at 50% -10%, rgba(59,130,246,0.10), transparent 60%), radial-gradient(800px 600px at 85% 100%, rgba(236,72,153,0.08), transparent 60%), #F8FAFC",
      }}
      data-testid="profile-page"
    >
      <header
        className="sticky top-0 z-30 backdrop-blur-xl"
        style={{
          backgroundColor: "rgba(248,250,252,0.85)",
          borderBottom: "1px solid rgba(148,163,184,0.18)",
        }}
      >
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center gap-2">
          <button onClick={onBack} className="p-1.5 rounded-full hover:bg-slate-100 transition-colors" data-testid="profile-back" aria-label="Back">
            <ArrowLeft weight="bold" className="w-5 h-5 text-slate-700" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.18em] font-black text-blue-600 leading-tight">My Profile</p>
            <h1 className="text-base font-black leading-tight text-slate-900 truncate" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif", letterSpacing: "-0.01em" }}>
              {firstName}
            </h1>
          </div>
          {onGoHome && (
            <button onClick={onGoHome} className="p-1.5 rounded-full hover:bg-slate-100 transition-colors" data-testid="profile-home-button" aria-label="Home" title="Home">
              <House weight="bold" className="w-5 h-5 text-slate-700" />
            </button>
          )}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        {/* Missing-photo nudge — shows a persistent amber card until the
            crew member uploads a profile photo. Tap anywhere on the card to
            jump straight to the photo picker. Hides the instant a photo is
            set. Added 2026-04-21 in response to "14 of 15 crew have no photo
            → stories audience + leaderboard show initials instead of faces." */}
        {!photo && (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="w-full rounded-2xl border border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50 p-4 flex items-center gap-3 text-left hover:shadow-md transition-shadow"
            data-testid="profile-photo-nudge"
          >
            <div className="w-11 h-11 rounded-full bg-amber-400 text-white flex items-center justify-center flex-shrink-0 shadow-sm">
              <Camera weight="fill" className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-black text-amber-900" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
                Add a profile photo
              </p>
              <p className="text-xs text-amber-800/80 leading-snug">
                Let the team see you in Stories, the Leaderboard, and the Wow Crew page. Takes 5 seconds.
              </p>
            </div>
            <span className="text-amber-700 font-black text-base">→</span>
          </button>
        )}

        {/* Photo */}
        <div className="dashboard-card flex flex-col items-center" data-testid="profile-photo-card">
          <div className="relative">
            {photo ? (
              <img src={photo} alt={firstName} className="w-28 h-28 rounded-full object-cover ring-4 ring-white shadow" data-testid="profile-photo-preview" />
            ) : (
              <div className="w-28 h-28 rounded-full flex items-center justify-center ring-4 ring-white shadow text-4xl font-black text-white" style={{ backgroundColor: "var(--fb-blue)", fontFamily: "'Five Below Gothic', Chivo, sans-serif" }} data-testid="profile-photo-initial">
                {initial}
              </div>
            )}
            <button
              onClick={() => fileRef.current?.click()}
              className="absolute bottom-0 right-0 w-9 h-9 rounded-full bg-white border border-slate-200 shadow flex items-center justify-center hover:bg-slate-50"
              data-testid="profile-photo-button"
              aria-label="Upload photo"
            >
              <Camera weight="fill" className="w-4 h-4" style={{ color: "var(--fb-blue)" }} />
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-3">Tap the camera to upload</p>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="profile-photo-input" />
          {photo && (
            <div className="flex items-center gap-3 mt-2">
              <button onClick={handleReCrop} className="text-xs hover:underline" style={{ color: "var(--fb-blue)" }} data-testid="profile-photo-adjust">Adjust / Crop</button>
              <span className="text-slate-200">•</span>
              <button onClick={() => setPhoto("")} className="text-xs text-red-600 hover:underline" data-testid="profile-photo-remove">Remove photo</button>
            </div>
          )}
        </div>

        {rawImage && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4" data-testid="crop-modal" onClick={() => !cropping && setRawImage(null)}>
            <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                <h3 className="text-base font-bold" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>Adjust Photo</h3>
                <button onClick={() => setRawImage(null)} disabled={cropping} className="p-1 rounded hover:bg-slate-100" aria-label="Close" data-testid="crop-close">
                  <X weight="bold" className="w-4 h-4" />
                </button>
              </div>
              <div className="relative w-full aspect-square bg-slate-900" data-testid="crop-area">
                <Cropper
                  image={rawImage}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  objectFit="contain"
                />
              </div>
              <div className="px-4 py-3 space-y-3">
                <div className="flex items-center gap-2">
                  <MagnifyingGlassPlus weight="bold" className="w-4 h-4 text-slate-500 flex-shrink-0" />
                  <input
                    type="range"
                    min="1"
                    max="4"
                    step="0.01"
                    value={zoom}
                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                    className="flex-1 accent-blue-600"
                    data-testid="crop-zoom-slider"
                  />
                  <span className="text-xs text-slate-400 w-10 text-right">{zoom.toFixed(2)}x</span>
                </div>
                <p className="text-xs text-slate-400 text-center">Drag to reposition, pinch or slide to zoom.</p>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={() => setRawImage(null)} disabled={cropping} data-testid="crop-cancel">Cancel</Button>
                  <Button size="sm" onClick={handleCropConfirm} disabled={cropping || !croppedPixels} className="fb-btn-primary" data-testid="crop-confirm">
                    {cropping ? "Applying…" : "Use this crop"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Name */}
        <div className="dashboard-card space-y-4">
          <div>
            <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
              <User weight="bold" className="w-3 h-3" /> First Name
            </label>
            <Input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Your first name"
              data-testid="profile-first-name"
            />
          </div>

          {/* "How do you let go and have fun?" — Five-Below-tagline bio
              line shown on the crew tile detail sheet so teammates can
              get to know each other. 140-char cap. (User ask 2026-05-25.) */}
          <div>
            <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
              <Sparkle weight="bold" className="w-3 h-3" /> How do you let go and have fun?
              <span className="ml-auto font-normal normal-case tracking-normal text-slate-400 tabular-nums">{funMotto.length}/140</span>
            </label>
            <Input
              value={funMotto}
              onChange={(e) => setFunMotto(e.target.value.slice(0, 140))}
              placeholder="Skiing on weekends · gaming · concerts · the kids…"
              data-testid="profile-fun-motto"
            />
            <p className="text-[11px] text-slate-400 mt-1 leading-tight">
              Shows on your crew tile when teammates tap it. Keep it light.
            </p>
          </div>

          <div>
            <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
              <Cake weight="bold" className="w-3 h-3" /> Birthday
              <span className="font-normal normal-case tracking-normal text-slate-400">(we only use month + day)</span>
            </label>
            {(() => {
              const MONTHS = [
                "January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December",
              ];
              const mm = birthday ? birthday.split("-")[0] : "";
              const dd = birthday ? birthday.split("-")[1] : "";
              const daysInMonth = mm
                ? new Date(2001, parseInt(mm), 0).getDate() // 2001 = non-leap, keeps Feb at 28
                : 31;
              const selectClass =
                "w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-blue-500";
              return (
                <div className="grid grid-cols-2 gap-2">
                  <select
                    className={selectClass}
                    value={mm}
                    onChange={(e) => {
                      const newM = e.target.value;
                      if (!newM) { setBirthday(""); return; }
                      // Clamp day to new month's max in case user switches from Aug 31 -> Feb.
                      const maxDay = new Date(2001, parseInt(newM), 0).getDate();
                      const currentD = parseInt(dd || "0", 10);
                      const safeD = currentD > 0 && currentD <= maxDay ? dd : "";
                      setBirthday(safeD ? `${newM}-${safeD}` : `${newM}-`);
                    }}
                    data-testid="profile-birthday-month"
                  >
                    <option value="">Month</option>
                    {MONTHS.map((name, i) => {
                      const val = String(i + 1).padStart(2, "0");
                      return <option key={val} value={val}>{name}</option>;
                    })}
                  </select>
                  <select
                    className={selectClass}
                    value={dd}
                    disabled={!mm}
                    onChange={(e) => {
                      const newD = e.target.value;
                      if (!mm) return;
                      setBirthday(newD ? `${mm}-${newD}` : `${mm}-`);
                    }}
                    data-testid="profile-birthday-day"
                  >
                    <option value="">{mm ? "Day" : "Pick month first"}</option>
                    {Array.from({ length: daysInMonth }, (_, i) => {
                      const val = String(i + 1).padStart(2, "0");
                      return <option key={val} value={val}>{i + 1}</option>;
                    })}
                  </select>
                </div>
              );
            })()}
            {birthday && birthday.length === 5 && birthday.split("-")[1] && (
              <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1">
                <Cake weight="fill" className="w-3.5 h-3.5 text-pink-500" /> {formatBirthday(birthday)}
              </p>
            )}
          </div>

          {/* Hire-date lock: once a crew member has a hire_date set AND has
              been in the system >48h, they can't save it directly. Instead,
              saving fires an Approve/Deny card to every manager in the Inbox;
              manager approval promotes the date. We still let the crew pick
              a new date here so the request carries the intended value.
              Managers see an unlocked field with no chip (they edit instantly).
              Display is gated client-side too; the server re-enforces. */}
          {(() => {
            const hoursSinceCreated = me?.created_at
              ? (Date.now() - new Date(me.created_at).getTime()) / 36e5
              : 9999;
            const isManager = !!user?.is_manager;
            const hasSetDate = !!me?.hire_date;
            const locked = !isManager && ((hasSetDate && hoursSinceCreated > 48) || (!hasSetDate && hoursSinceCreated > 48));
            const graceHoursLeft = Math.max(0, 48 - hoursSinceCreated);
            const hasPending = !!me?.hire_date_pending;
            return (
              <div>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 flex-wrap">
                  <Briefcase weight="bold" className="w-3 h-3" /> Work Anniversary
                  {hasPending ? (
                    <span className="font-bold normal-case tracking-normal text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5" data-testid="anniversary-pending-tag">
                      Pending manager approval
                    </span>
                  ) : locked ? (
                    <span className="font-bold normal-case tracking-normal text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid="anniversary-locked-tag">
                      Locked · Requires approval
                    </span>
                  ) : !hasSetDate && !isManager ? (
                    <span className="font-bold normal-case tracking-normal text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="anniversary-grace-tag">
                      {graceHoursLeft < 1 ? "Less than 1h to set" : `${Math.floor(graceHoursLeft)}h left to set`}
                    </span>
                  ) : null}
                </label>
                <Input
                  type="date"
                  value={hireDate}
                  onChange={(e) => setHireDate(e.target.value)}
                  max={new Date().toLocaleDateString("en-CA", { timeZone: "America/Denver" })}
                  disabled={hasPending}
                  data-testid="profile-hire-date"
                  title={hasPending ? "A request is already pending — ask your manager to approve or deny." : ""}
                />
                {hasPending && (
                  <p className="text-[11px] text-blue-700 mt-1 flex items-center gap-1" data-testid="anniversary-pending-note">
                    Requested <span className="font-semibold">{me.hire_date_pending}</span> — waiting on manager approval.
                  </p>
                )}
                {!hasPending && locked && (
                  <p className="text-[11px] text-slate-500 mt-1">
                    Pick the date you want and tap Save — a manager will approve it in their Inbox.
                  </p>
                )}
              </div>
            );
          })()}

          {(() => {
            const hoursSinceCreated = me?.created_at
              ? (Date.now() - new Date(me.created_at).getTime()) / 36e5
              : 9999;
            const isManager = !!user?.is_manager;
            const hasSetDate = !!me?.hire_date;
            const locked = !isManager && ((hasSetDate && hoursSinceCreated > 48) || (!hasSetDate && hoursSinceCreated > 48));
            const hasPending = !!me?.hire_date_pending;
            // When locked + user changed the date, this tap fires a pending
            // request instead of an instant save — relabel so it's obvious.
            const wantsDateChange = locked && !hasPending && hireDate && hireDate !== (me?.hire_date || "");
            return (
              <Button
                onClick={handleSave}
                disabled={saving}
                className="w-full fb-btn-primary h-11"
                data-testid="profile-save"
              >
                <FloppyDisk weight="bold" className="w-4 h-4 mr-1" />{" "}
                {saving ? "Saving…" : (wantsDateChange ? "Request Change" : "Save Profile")}
              </Button>
            );
          })()}
        </div>

        <RecognitionReportCard crewId={user.crew_id} />

        <ProfileHighlights
          crewId={user.crew_id}
          viewerId={user.crew_id}
          isManager={!!user?.is_manager}
        />

        {/* Personal Preferences — manager-only utilities that don't fit
            anywhere else. Today: the "Edit Dashboard Layout" toggle.
            We hid the inline button from the Mission Control surface
            in Wave A.6 (2026-06-13) so the dashboard reads clean; this
            toggle is how managers re-enable reorder mode. Flipping it
            on sets `localStorage.wow_edit_layout = "1"` and bounces
            the manager home so the dashboard mounts in edit mode.
            "Done" on the dashboard clears the flag again. */}
        {user?.is_manager && (
          <PersonalPreferencesCard onGoHome={onGoHome} />
        )}

        <p className="text-xs text-slate-400 text-center">
          Your role, employee ID, and login are managed by an admin.<br />
          Notification preferences live in <span className="font-semibold">Settings → Notifications</span>.
        </p>
      </main>
    </div>
  );
}

// --- Personal Preferences (manager-only) ------------------------------------

/** Toggle for the dashboard reorder mode. Reads + writes
 *  `localStorage.wow_edit_layout` ("1" = on). Flipping ON also pops the
 *  manager back to the Mission Control dashboard so they can drag
 *  tiles immediately. The dashboard's "Done" button clears the flag. */
function PersonalPreferencesCard({ onGoHome }) {
  const [enabled, setEnabled] = useState(() => {
    try { return localStorage.getItem("wow_edit_layout") === "1"; } catch { return false; }
  });
  const [autoFix, setAutoFix] = useState(() => {
    try { return localStorage.getItem("wow:ai_autofix") === "1"; } catch { return false; }
  });

  const toggleAutoFix = () => {
    const next = !autoFix;
    try {
      if (next) localStorage.setItem("wow:ai_autofix", "1");
      else localStorage.removeItem("wow:ai_autofix");
    } catch { /* quota */ }
    setAutoFix(next);
    toast.success(next ? "AI will polish every message before it sends ✨" : "Auto-fix off — use the ✨ button anytime");
  };

  const toggle = () => {
    const next = !enabled;
    try {
      if (next) localStorage.setItem("wow_edit_layout", "1");
      else localStorage.removeItem("wow_edit_layout");
    } catch { /* quota */ }
    setEnabled(next);
    if (next) {
      toast.success("Dashboard reorder enabled — taking you there…");
      // Small delay so the toast lands before we navigate away.
      setTimeout(() => onGoHome?.(), 350);
    } else {
      toast.success("Reorder disabled");
    }
  };

  return (
    <div className="dashboard-card space-y-3" data-testid="personal-preferences-card">
      <p className="text-xs font-black tracking-wider text-slate-500 uppercase">Personal Preferences</p>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center flex-shrink-0">
          <DotsSixVertical weight="bold" className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-black text-slate-900" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
            Edit dashboard layout
          </p>
          <p className="text-[11px] text-slate-500 leading-snug">
            Show drag handles on Mission Control so you can reorder tiles. Turn off when you&apos;re done.
          </p>
        </div>
        <button
          type="button"
          onClick={toggle}
          role="switch"
          aria-checked={enabled}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${enabled ? "bg-blue-600" : "bg-slate-300"}`}
          data-testid="pref-edit-layout-toggle"
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-5" : "translate-x-0.5"}`}
          />
        </button>
      </div>
      <div className="flex items-center gap-3 pt-3 border-t border-slate-100">
        <div className="w-10 h-10 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center flex-shrink-0">
          <Sparkle weight="fill" className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-black text-slate-900" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
            AI proofread on send
          </p>
          <p className="text-[11px] text-slate-500 leading-snug">
            Auto-fix spelling, grammar &amp; punctuation before each message sends. Your slang and tone stay yours. Tap the ✨ in any chat to fix just once.
          </p>
        </div>
        <button
          type="button"
          onClick={toggleAutoFix}
          role="switch"
          aria-checked={autoFix}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 ${autoFix ? "bg-violet-600" : "bg-slate-300"}`}
          data-testid="pref-ai-autofix-toggle"
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${autoFix ? "translate-x-5" : "translate-x-0.5"}`}
          />
        </button>
      </div>
    </div>
  );
}

// --- Profile highlights -----------------------------------------------------

/** Reusable Instagram-style collection bubble — circular cover image with
 *  the name underneath. Active state shows a blue ring + bold label.
 *  Falls back to a soft slate gradient with the initial letter when no
 *  cover_url is set (no more yellow per user feedback 2026-02-22). */
function CollectionBubble({ label, count, coverUrl, active, onClick, testId, fallbackInitial }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex-shrink-0 flex flex-col items-center gap-1 focus:outline-none w-16"
      data-testid={testId}
    >
      <div className={`w-16 h-16 rounded-full overflow-hidden flex items-center justify-center transition-all ${
        active ? "ring-[3px] ring-blue-500 ring-offset-2 ring-offset-white" : "ring-2 ring-slate-200"
      } ${coverUrl ? "bg-slate-100" : "bg-gradient-to-br from-slate-100 to-slate-200"}`}>
        {coverUrl ? (
          <img src={coverUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <span className="text-2xl font-black text-slate-500">{fallbackInitial}</span>
        )}
      </div>
      <span className={`text-[10px] truncate max-w-[64px] ${active ? "font-black text-slate-900" : "font-bold text-slate-600"}`}>
        {label}
      </span>
      <span className="text-[9px] text-slate-400 -mt-1">{count}</span>
    </button>
  );
}

// Persistent archive of a crew member's favorite stories — grouped into
// crew-owned named collections (max 6) per spec 2026-02-22. The strip uses
// the LEAN endpoint (no base64 media) so it renders in <200ms even with
// 100+ saved highlights. Tapping a thumbnail fetches the full media on
// demand from /api/highlights/{id}. sessionStorage caches the lean list +
// the collection summary so re-opening a profile is instant.
function ProfileHighlights({ crewId, viewerId, isManager }) {
  const cacheKeyHighlights = `highlights:lean:${crewId}`;
  const cacheKeyCollections = `highlight-collections:${crewId}`;
  const [highlights, setHighlights] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(cacheKeyHighlights) || "[]"); } catch { return []; }
  });
  const [collectionsSummary, setCollectionsSummary] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(cacheKeyCollections) || "null"); } catch { return null; }
  });
  const [selectedCol, setSelectedCol] = useState("__all__");  // "__all__" | "__uncategorized__" | <id>
  const [active, setActive] = useState(null);    // { ...lean }
  const [activeMedia, setActiveMedia] = useState(""); // resolved photo_url after on-tap fetch
  const [isMine] = useState(crewId === viewerId);
  const [managerOpen, setManagerOpen] = useState(false); // toggle the collection-management drawer
  const [newColName, setNewColName] = useState("");

  const load = useCallback(async () => {
    try {
      const [hRes, cRes] = await Promise.all([
        axios.get(`${API}/crew/${crewId}/highlights`, { params: { lean: true } }),
        axios.get(`${API}/crew/${crewId}/highlight-collections`),
      ]);
      const fresh = hRes.data || [];
      setHighlights(fresh);
      setCollectionsSummary(cRes.data || null);
      try {
        sessionStorage.setItem(cacheKeyHighlights, JSON.stringify(fresh));
        sessionStorage.setItem(cacheKeyCollections, JSON.stringify(cRes.data || null));
      } catch { /* ignore */ }
    } catch (e) { /* silent */ }
  }, [crewId, cacheKeyHighlights, cacheKeyCollections]);
  useEffect(() => { load(); }, [load]);

  // Resolve full media on tap. We don't preload — the heavy base64 only
  // hits the wire when the user actually opens a thumbnail.
  const openHighlight = async (h) => {
    setActive(h);
    setActiveMedia("");  // show loading state in the modal
    try {
      const r = await axios.get(`${API}/highlights/${h.id}`);
      setActiveMedia(r.data?.photo_url || "");
    } catch (e) {
      toast.error("Couldn't load highlight");
      setActive(null);
    }
  };

  if (!highlights.length && !isMine) return null;

  const canDelete = (h) => isManager || h.author_id === viewerId;

  const onDelete = async (h) => {
    if (!window.confirm("Remove this highlight?")) return;
    try {
      await axios.delete(`${API}/highlights/${h.id}`, { params: { crew_id: viewerId } });
      toast.success("Highlight removed");
      setActive(null);
      setActiveMedia("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't remove");
    }
  };

  const createCollection = async () => {
    const name = newColName.trim();
    if (!name) return;
    try {
      await axios.post(`${API}/crew/${crewId}/highlight-collections`, { crew_id: viewerId, name });
      setNewColName("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't create");
    }
  };

  const renameCollection = async (c) => {
    const name = window.prompt(`Rename "${c.name}" to:`, c.name);
    if (!name || !name.trim() || name.trim() === c.name) return;
    try {
      await axios.put(`${API}/highlight-collections/${c.id}`, { crew_id: viewerId, name: name.trim() });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't rename");
    }
  };

  const deleteCollection = async (c) => {
    if (!window.confirm(`Delete the "${c.name}" collection? Highlights inside will fall back to "All" — nothing is deleted permanently.`)) return;
    try {
      await axios.delete(`${API}/highlight-collections/${c.id}`, { params: { crew_id: viewerId } });
      if (selectedCol === c.id) setSelectedCol("__all__");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't delete");
    }
  };

  // Client-side resize → small JPEG suitable for use as a collection cover.
  // Keeps the cover under 30 KB so all 6 bubbles can render with no perf hit.
  // Videos use the FIRST FRAME captured via a hidden <video> element +
  // canvas paint — same trick as Instagram's highlight covers.
  const buildCoverDataUrl = async (mediaUrl, mediaType) => {
    return new Promise((resolve, reject) => {
      const draw = (source, sourceWidth, sourceHeight) => {
        const max = 200;
        const ratio = Math.min(1, max / Math.max(sourceWidth, sourceHeight));
        const w = Math.max(1, Math.round(sourceWidth * ratio));
        const h = Math.max(1, Math.round(sourceHeight * ratio));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(source, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.75));
      };
      if (mediaType === "video") {
        const v = document.createElement("video");
        v.src = mediaUrl;
        v.crossOrigin = "anonymous";
        v.muted = true;
        v.playsInline = true;
        v.onloadeddata = () => {
          // Some iOS Safari versions need a tick before the first frame is
          // actually paintable to canvas — bump a frame seek to ensure it.
          v.currentTime = 0.1;
        };
        v.onseeked = () => draw(v, v.videoWidth, v.videoHeight);
        v.onerror = () => reject(new Error("Video frame capture failed"));
      } else {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => draw(img, img.width, img.height);
        img.onerror = () => reject(new Error("Image load failed"));
        img.src = mediaUrl;
      }
    });
  };

  const setAsCover = async () => {
    if (!active || !activeMedia) return;
    const collectionId = active.collection_id;
    if (!collectionId) {
      toast.error("Move this highlight into a collection first.");
      return;
    }
    try {
      const cover = await buildCoverDataUrl(activeMedia, active.media_type);
      await axios.put(`${API}/highlight-collections/${collectionId}/cover`, {
        crew_id: viewerId,
        cover_url: cover,
      });
      toast.success(`Set as cover for "${active.collection_name}"`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Couldn't set cover");
    }
  };

  // Filter the lean list client-side based on which bubble is selected.
  const filtered = highlights.filter((h) => {
    if (selectedCol === "__all__") return true;
    if (selectedCol === "__uncategorized__") return !h.collection_id;
    return h.collection_id === selectedCol;
  });

  const cols = collectionsSummary?.collections || [];
  const totalCount = collectionsSummary?.total_count ?? highlights.length;
  const uncategorizedCount = collectionsSummary?.uncategorized_count ?? 0;

  return (
    <div className="space-y-2" data-testid="profile-highlights">
      <div className="flex items-center justify-between">
        <p className="text-xs font-black tracking-wider text-slate-500 uppercase">Highlights</p>
        {isMine && (
          <button
            type="button"
            onClick={() => setManagerOpen((o) => !o)}
            className="text-[10px] font-bold uppercase tracking-wider text-blue-600 hover:underline"
            data-testid="highlight-manage-toggle"
          >
            {managerOpen ? "Done" : "Manage"}
          </button>
        )}
      </div>

      {/* Collection bubbles — All, Uncategorized (when there are some), then
          the user's named collections, Instagram-style. Tapping filters the
          strip below. Each bubble shows the user-chosen cover image (or a
          gradient + initial when no cover is set yet). */}
      {(cols.length > 0 || uncategorizedCount > 0) && (
        <div className="flex gap-3 overflow-x-auto pb-2 pt-1 scrollbar-hide">
          <CollectionBubble label="All" count={totalCount} active={selectedCol === "__all__"} onClick={() => setSelectedCol("__all__")} testId="col-all" fallbackInitial="★" />
          {uncategorizedCount > 0 && (
            <CollectionBubble label="Other" count={uncategorizedCount} active={selectedCol === "__uncategorized__"} onClick={() => setSelectedCol("__uncategorized__")} testId="col-uncat" fallbackInitial="·" />
          )}
          {cols.map((c) => (
            <CollectionBubble
              key={c.id}
              label={c.name}
              count={c.count}
              coverUrl={c.cover_url || ""}
              active={selectedCol === c.id}
              onClick={() => setSelectedCol(c.id)}
              testId={`col-${c.id}`}
              fallbackInitial={(c.name || "?").trim().slice(0, 1).toUpperCase()}
            />
          ))}
        </div>
      )}

      {/* Manage drawer (only on the owner's own profile) */}
      {isMine && managerOpen && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="highlight-manager">
          {cols.length === 0 && (
            <p className="text-xs text-slate-500 italic">No collections yet — create one below.</p>
          )}
          {cols.map((c) => (
            <div key={c.id} className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-slate-100">
              <span className="flex-1 text-sm font-bold truncate">{c.name}</span>
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{c.count}</span>
              <button
                type="button"
                onClick={() => renameCollection(c)}
                className="text-xs font-bold text-blue-600 hover:underline"
                data-testid={`col-rename-${c.id}`}
              >
                Rename
              </button>
              <button
                type="button"
                onClick={() => deleteCollection(c)}
                className="text-xs font-bold text-rose-600 hover:underline"
                data-testid={`col-delete-${c.id}`}
              >
                Delete
              </button>
            </div>
          ))}
          {cols.length < 6 && (
            <div className="flex gap-2">
              <input
                type="text"
                value={newColName}
                onChange={(e) => setNewColName(e.target.value.slice(0, 30))}
                placeholder="New collection name…"
                className="flex-1 h-9 rounded-md border border-slate-200 px-3 text-sm focus:outline-none focus:border-blue-400"
                data-testid="col-new-name"
              />
              <button
                type="button"
                onClick={createCollection}
                disabled={!newColName.trim()}
                className="h-9 px-3 rounded-md bg-blue-600 text-white text-xs font-bold disabled:opacity-50"
                data-testid="col-create"
              >
                Create
              </button>
            </div>
          )}
          <p className="text-[10px] text-slate-500">{cols.length}/6 collections used. Each crew owns their own.</p>
        </div>
      )}

      {/* Thumbnails — render the actual saved photo (resized server-side
          to a tiny ~10-20 KB JPEG `thumb_url`). Videos fall back to a slate
          gradient with a play icon overlay since we don't generate video
          thumbs server-side. Removed the yellow gradient + initial letter
          fallback per user feedback 2026-02-22 ("ugly yellow"). */}
      {filtered.length === 0 ? (
        <p className="text-xs text-slate-400 italic">{selectedCol === "__all__" ? "No saved highlights yet." : "Nothing in this collection yet."}</p>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
          {filtered.map((h) => (
            <button
              key={h.id}
              type="button"
              onClick={() => openHighlight(h)}
              className="flex-shrink-0 w-16 flex flex-col items-center gap-1 focus:outline-none"
              data-testid={`highlight-${h.id}`}
            >
              <div className="relative w-16 h-16 rounded-full overflow-hidden ring-2 ring-slate-200 hover:ring-blue-400 transition-all bg-slate-100">
                {h.thumb_url ? (
                  <img src={h.thumb_url} alt="" className="w-full h-full object-cover" />
                ) : h.media_type === "video" ? (
                  <div className="w-full h-full bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
                    <PlayCircle weight="fill" className="w-7 h-7 text-white/90" />
                  </div>
                ) : (
                  <div className="w-full h-full bg-slate-200" />
                )}
                {h.media_type === "video" && h.thumb_url && (
                  <span className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <PlayCircle weight="fill" className="w-6 h-6 text-white drop-shadow" />
                  </span>
                )}
              </div>
              <span className="text-[10px] font-bold text-slate-600 truncate max-w-[64px]">
                {h.caption ? h.caption.slice(0, 12) : (h.collection_name || "Story")}
              </span>
            </button>
          ))}
        </div>
      )}

      {active && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => { setActive(null); setActiveMedia(""); }} data-testid="highlight-viewer">
          <div className="relative w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            {activeMedia ? (
              active.media_type === "video" ? (
                <video src={activeMedia} controls autoPlay playsInline className="w-full rounded-xl" />
              ) : (
                <img src={activeMedia} alt="" className="w-full rounded-xl" />
              )
            ) : (
              <div className="w-full aspect-[4/5] rounded-xl bg-slate-800 animate-pulse" />
            )}
            {active.caption && (
              <div className="mt-3 text-white text-sm bg-black/40 rounded-lg px-3 py-2">{active.caption}</div>
            )}
            {active.collection_name && (
              <div className="mt-1 text-yellow-300 text-[11px] uppercase tracking-wider font-black">★ {active.collection_name}</div>
            )}
            <div className="mt-3 flex items-center justify-between text-white">
              <button onClick={() => { setActive(null); setActiveMedia(""); }} className="text-xs font-bold underline">Close</button>
              <div className="flex items-center gap-3">
                {isMine && active.collection_id && activeMedia && (
                  <button
                    onClick={setAsCover}
                    className="text-xs font-bold text-yellow-300 hover:text-yellow-200"
                    data-testid="highlight-set-cover"
                    title="Use this image as the cover for its collection"
                  >
                    Set as cover
                  </button>
                )}
                {canDelete(active) && (
                  <button
                    onClick={() => onDelete(active)}
                  className="text-xs font-bold text-rose-300 hover:text-rose-200"
                  data-testid="highlight-delete"
                >
                  Remove
                </button>
              )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Recognition Report Card (High Fives tally on Profile) ────────────
function RecognitionReportCard({ crewId }) {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);     // null = not opened yet
  const [histTotal, setHistTotal] = useState(0);
  const [histMore, setHistMore] = useState(false);
  const [histLoading, setHistLoading] = useState(false);
  useEffect(() => {
    if (!crewId) return;
    axios
      .get(`${API}/recognition/report-card/${crewId}`, { params: { actor_id: crewId } })
      .then((r) => setData(r.data))
      .catch(() => {});
  }, [crewId]);

  const loadHistory = async (skip = 0) => {
    setHistLoading(true);
    try {
      const r = await axios.get(`${API}/recognition/history/${crewId}`, { params: { actor_id: crewId, skip, limit: 20 } });
      setHistTotal(r.data.total);
      setHistMore(r.data.has_more);
      setHistory((prev) => (skip === 0 ? r.data.cards : [...(prev || []), ...r.data.cards]));
    } catch (_) { /* ignore */ }
    finally { setHistLoading(false); }
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
    catch (_) { return ""; }
  };

  if (!data) return null;
  const maxCount = Math.max(1, ...data.breakdown.map((b) => b.count));

  return (
    <div className="dashboard-card space-y-4" data-testid="profile-recognition-card">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-black uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
          <Sparkle weight="fill" className="w-4 h-4 text-pink-500" /> Recognition Report Card
        </h3>
        <span className="text-[11px] text-slate-400 font-bold">{data.month_label}</span>
      </div>

      <div className="flex items-end gap-3">
        <div className="text-center">
          <p className="text-4xl font-black leading-none" style={{ color: "var(--fb-blue)", fontFamily: "'Five Below Gothic', Chivo, sans-serif" }} data-testid="profile-recognition-month-total">
            {data.month_total}
          </p>
          <p className="text-[11px] font-bold text-slate-500 mt-1">High Fives<br />this month</p>
        </div>
        <div className="flex-1 flex items-center justify-end gap-4 pb-1">
          <div className="text-right">
            <p className="text-lg font-black text-slate-800">{data.all_time}</p>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">All-time</p>
          </div>
          <div className="text-right">
            <p className="text-lg font-black text-slate-800">{data.given}</p>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Given</p>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {data.breakdown.map((b) => (
          <div key={b.key} className="flex items-center gap-2" data-testid={`profile-recognition-value-${b.key}`}>
            <span className="text-sm w-5 text-center">{b.emoji}</span>
            <span className="text-[12px] font-bold text-slate-700 w-36 truncate">{b.label}</span>
            <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${(b.count / maxCount) * 100}%`, backgroundColor: b.color }} />
            </div>
            <span className="text-[12px] font-black text-slate-800 w-5 text-right">{b.count}</span>
          </div>
        ))}
      </div>

      {data.recent.length > 0 && history === null && (
        <div className="pt-1 space-y-1.5">
          <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Recent shout-outs</p>
          {data.recent.slice(0, 3).map((c) => (
            <div key={c.id} className="text-[12px] text-slate-600 bg-slate-50 rounded-xl px-3 py-2" data-testid={`profile-recognition-recent-${c.id}`}>
              <span className="font-bold text-slate-800">{c.value_emoji} {c.from_name}</span>
              {c.note ? <span> — “{c.note}”</span> : <span className="text-slate-400"> recognized you for {c.value_label}</span>}
            </div>
          ))}
        </div>
      )}

      {data.all_time > 0 && history === null && (
        <button
          onClick={() => loadHistory(0)}
          disabled={histLoading}
          className="w-full text-[12px] font-black text-blue-600 hover:text-blue-700 py-1.5 rounded-lg hover:bg-blue-50 transition-colors"
          data-testid="profile-recognition-viewall"
        >
          {histLoading ? "Loading…" : `View all ${data.all_time} recognition${data.all_time === 1 ? "" : "s"} →`}
        </button>
      )}

      {history !== null && (
        <div className="pt-1 space-y-1.5" data-testid="profile-recognition-history">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">All recognitions ({histTotal})</p>
            <button onClick={() => setHistory(null)} className="text-[11px] font-bold text-slate-400 hover:text-slate-600" data-testid="profile-recognition-collapse">Collapse</button>
          </div>
          {history.map((c) => (
            <div key={c.id} className="text-[12px] text-slate-600 bg-slate-50 rounded-xl px-3 py-2" data-testid={`profile-recognition-hist-${c.id}`}>
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-bold text-slate-800">{c.value_emoji} {c.from_name}</span>
                <span className="text-[10px] text-slate-400 flex-shrink-0">{fmtDate(c.created_at)}</span>
              </div>
              {c.note ? <p className="leading-snug">“{c.note}”</p> : <p className="text-slate-400">recognized you for {c.value_label}</p>}
            </div>
          ))}
          {histMore && (
            <button
              onClick={() => loadHistory(history.length)}
              disabled={histLoading}
              className="w-full text-[12px] font-black text-blue-600 hover:text-blue-700 py-1.5 rounded-lg hover:bg-blue-50 transition-colors"
              data-testid="profile-recognition-loadmore"
            >
              {histLoading ? "Loading…" : "Load more"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
